import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, shareReplay, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { normalizeToObservable } from '@mwe/utils';
// eslint-disable-next-line
import { isFinished } from '../../../../../features/wizard/src/lib/models/wizard-step-state.model';

@Component({
  selector: 'mwe-progress-bar',
  templateUrl: './progress-bar.component.html',
  imports: [AsyncPipe, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressBarComponent implements AfterViewInit {
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly selectedIndex = input.required<number>();
  readonly title = input.required<string | Observable<string>>();

  readonly labels = input<string[]>(undefined);
  readonly debug = input<boolean>(undefined);
  readonly steps = input<
    {
      step: any; // IWizardStep
      label$: Observable<string>;
      canSkip$: Observable<boolean>;
      canEnter$: Observable<boolean>;
      focusable$: Observable<boolean>;
      depsFulfilled$: Observable<boolean>;
      state: number;
      stateTags: string;
    }[]
  >(undefined);

  readonly requestJump = output<any>(); // IWizardStep

  private _offset = 0;

  readonly processTitle = toSignal(toObservable(this.title).pipe(switchMap(title => normalizeToObservable(title))));

  readonly stepLabels = computed(() => this.steps()?.map(step => normalizeToObservable(step.label$).pipe(shareReplay(1))));
  readonly hasLabels = computed<boolean>(() => this.labels()?.length > 0 || this.stepLabels()?.length > 0);

  ngAfterViewInit(): void {
    this.addClickHandlers();
    this._offset = this.measureOverhang();
    this.onResize();
  }

  onKeyDown(event: KeyboardEvent, step: any): void {
    if (event.key === 'Enter') this.requestJump.emit(step); // IWizardStep
  }

  getStatus(index: number, isCurrent: boolean): string {
    let status: string;

    // as current steps is neither unfinished nor finished, no status should be announced
    if (isCurrent) return '';

    if (this.steps()) {
      status = isFinished(this.steps()[index]?.state) && this.selectedIndex() > index ? 'finished' : 'unfinished';
    } else {
      status = this.selectedIndex() > index ? 'finished' : 'unfinished';
    }

    return `global.wizard.progressbar.${status}`;
  }

  private addClickHandlers(): void {
    // adding a "(click)" handler to the HTML element indicates to some screen readers that the element may be clickable
    // even if it is programmatically disabled - adding aria-disabled will not fully solve this issues as this is semantically incorrect
    // manually adding click handler via DOM solves this issue

    this.steps()?.forEach((step, index) => {
      const listItem = document.getElementById('progress-bar-step-' + index);
      if (!listItem) return;

      let attached: boolean;
      const handler = () => this.requestJump.emit(step.step);
      const add = () => {
        if (attached) return;

        attached = true;
        listItem.addEventListener('click', handler);
      };
      const remove = () => {
        if (!attached) return;

        attached = false;
        listItem.removeEventListener('click', handler);
      };

      step.focusable$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap(isFocusable => {
            if (isFocusable) add();
          }),
        )
        .subscribe();

      this.destroyRef.onDestroy(() => remove());
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event?: unknown): void {
    if (this._offset <= 0) {
      return;
    }

    const elementRect = this.elementRef.nativeElement.getBoundingClientRect();

    if (elementRect.left < this._offset) {
      this.elementRef.nativeElement.classList.add('step-navigation-flush-labels');
    } else {
      this.elementRef.nativeElement.classList.remove('step-navigation-flush-labels');
    }

    if (this.checkStepOverlap()) {
      this.elementRef.nativeElement.classList.add('step-navigation-one-label');
    } else {
      this.elementRef.nativeElement.classList.remove('step-navigation-one-label');
    }
  }

  private measureOverhang(): number {
    const elementRect = this.elementRef.nativeElement.getBoundingClientRect();
    const testArr = [
      this.elementRef.nativeElement.querySelector('.step-navigation-step:first-child .step-navigation-step-label').getBoundingClientRect(),
      this.elementRef.nativeElement.querySelector('.step-navigation-step:last-child .step-navigation-step-label').getBoundingClientRect(),
    ];

    return Math.max(
      ...testArr.map(e => {
        return Math.max(...[elementRect.left - e.left, e.right - elementRect.right]);
      }),
    );
  }

  private checkStepOverlap(): boolean {
    const testArr = [...this.elementRef.nativeElement.querySelectorAll('.step-navigation-step-label')].map(e => e.getBoundingClientRect());
    // checks overlap between neighboring elements

    for (let i = 0; i < testArr.length - 1; i++) {
      if (testArr[i].right > testArr[i + 1].left) {
        return true;
      }
    }

    return false;
  }
}
